import { HttpBackend, HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, map, share, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PlatformAuthTokenService } from '../services/platform-auth-token.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private readonly tokens = inject(PlatformAuthTokenService);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private refresh: Observable<string> | null = null;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(environment.apiBaseUrl)) {
      return next.handle(request);
    }

    const authPrefix = `${environment.apiBaseUrl}/candidate/auth`;
    const platformPrefix = `${environment.apiBaseUrl}/candidate/`;
    if (request.url.startsWith(authPrefix)) {
      return next.handle(request.clone({ withCredentials: true }));
    }
    if (!request.url.startsWith(platformPrefix)) {
      return next.handle(request);
    }

    return next.handle(this.withToken(request)).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
          return throwError(() => error);
        }
        return this.refreshToken().pipe(
          switchMap((token) => next.handle(this.withToken(request, token))),
          catchError((refreshError: unknown) => {
            this.tokens.set(null);
            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  /**
   * Adds the candidate bearer token when one is stored.
   * @param request - Outgoing request
   * @param token - Optional token override after a refresh
   * @returns Cloned request
   */
  private withToken(request: HttpRequest<unknown>, token = this.tokens.token()): HttpRequest<unknown> {
    if (!token) {
      return request.clone({ withCredentials: true });
    }
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
  }

  /**
   * Refreshes the JWT with the httpOnly cookie, bypassing this interceptor.
   * @returns Observable emitting the new JWT
   */
  private refreshToken(): Observable<string> {
    if (!this.refresh) {
      this.refresh = this.rawHttp
        .post<{ jwt: string }>(`${environment.apiBaseUrl}/candidate/auth/refresh-token`, {}, { withCredentials: true })
        .pipe(
          map((response) => {
            if (!response.jwt) {
              throw new Error('Token refresh failed');
            }
            this.tokens.set(response.jwt);
            return response.jwt;
          }),
          finalize(() => {
            this.refresh = null;
          }),
          share()
        );
    }
    return this.refresh;
  }
}
